// ============================================================================
// GsTooltip — primitive tooltip moderne 2026 (@gs/ui, sur Radix)
// ============================================================================
// MASTER-DS-TOOLTIP-CONTAINERS 2026-06-16 : remplace les `title` natifs informatifs
// (lents ~1.5 s, non stylables, invisibles au clavier/tactile) par une surface DS
// (tokens, dark mode, a11y). Primitive PURE et domaine-agnostique : la couche metier
// (RoleMatrix P2) lui passe son contenu (label + description + conteneur/prefixe).
//
// Pas de t() dans @gs/ui (pattern GsNotificationBellLabels) : le contenu est passe par
// l'appelant, deja traduit. Surface = GS_COMPONENTS.tooltip (tokens, zero hex en dur).
// Radix gere le focus clavier, le tactile (long-press) et role=tooltip.
import type { ReactNode } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { GS_COMPONENTS, GS_TYPOGRAPHY } from '../tokens';

export interface GsTooltipProviderProps {
  children: ReactNode;
  /** Delai d'ouverture au survol, en ms (defaut 200). */
  delayDuration?: number;
}

/**
 * Enveloppe Tooltip.Provider — A MONTER UNE FOIS haut dans l'app (shell). Ouverture
 * a 200 ms au survol puis instantanee tant qu'on reste dans le groupe
 * (skipDelayDuration=0). P2 le placera dans le shell de la Console UG.
 */
export function GsTooltipProvider({ children, delayDuration = 200 }: GsTooltipProviderProps) {
  return (
    <Tooltip.Provider delayDuration={delayDuration} skipDelayDuration={0}>
      {children}
    </Tooltip.Provider>
  );
}

export interface GsTooltipProps {
  /** Contenu du tooltip (texte simple ou <GsRichTooltipContent>). */
  content: ReactNode;
  /** Element declencheur — recoit le comportement Radix via asChild. */
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Rend l'enfant nu, sans tooltip (ex: libelle deja entierement visible). */
  disabled?: boolean;
}

/**
 * Tooltip generique. Anim fade + 2 px (animate-tooltip-in, neutralisee par
 * motion-reduce). Surface elevee claire en mode clair, slate-800 en sombre.
 */
export function GsTooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  disabled,
}: GsTooltipProps) {
  if (disabled) return <>{children}</>;
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          role="tooltip"
          className={
            `${GS_COMPONENTS.tooltip} ` +
            'data-[state=delayed-open]:animate-tooltip-in ' +
            'data-[state=instant-open]:animate-tooltip-in motion-reduce:animate-none'
          }
        >
          {content}
          <Tooltip.Arrow className={GS_COMPONENTS.tooltipArrow} width={10} height={5} />
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export interface GsRichTooltipContentProps {
  /** Titre gras (ex: libelle du module). */
  title: string;
  /** Phrase descriptive (corps). */
  description?: string;
  /** Ligne meta (chips : conteneur, prefixe, statut). */
  meta?: ReactNode;
}

/**
 * Contenu riche presentational et domaine-agnostique : titre gras + phrase + ligne
 * meta (chips). P2 lui passe label + description + conteneur/prefixe/pastille statut.
 */
export function GsRichTooltipContent({ title, description, meta }: GsRichTooltipContentProps) {
  return (
    <div className="flex flex-col gap-1">
      <span className={GS_TYPOGRAPHY.hierarchy.tooltipTitle}>{title}</span>
      {description ? (
        <span className={GS_TYPOGRAPHY.hierarchy.tooltipDescription}>{description}</span>
      ) : null}
      {meta ? (
        <span className={`mt-0.5 flex flex-wrap items-center gap-1 ${GS_TYPOGRAPHY.hierarchy.tooltipMeta}`}>
          {meta}
        </span>
      ) : null}
    </div>
  );
}
